/** ***********
*
* <request-for-rights.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, Subscription, throwError } from 'rxjs';
import { FamilyRight, ParentFamilyRight } from 'src/app/models/family';
import { User } from 'src/app/models/user';
import { AskRightDialogData, AskRightDialogResult, FamilyManagers, DeleteMyRightsPayload, RemoveRolesFromParentFamilyOrFamily, RoleLevelEnum, FamilyRightCache } from 'src/app/models/user-right';
import { FamilyService } from 'src/app/services/family.service';
import { ParentFamilyService } from 'src/app/services/parent-family.service';
import { UserRightService } from 'src/app/services/user-right.service';
import { selectParentFamilyRightFilters$ } from '../../../store/selectors/filter.selectors';
import { selectManagers$, selectMyRightsTree$, selectRightRequests$, selectAllUsers$ } from 'src/app/store/selectors/user-right.selectors';
import { DialogAskRoleComponent } from '../dialog/dialog-ask-role/dialog-ask-role.component';
import { selectCompaniesState$ } from 'src/app/store/selectors/company.selectors';
import { selectSiteState$ } from 'src/app/store/selectors/site.selectors';
import { cloneDeep } from 'lodash';
import { Actions as UserGroupActions } from '../_shared/user-groups/user-groups.component';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { ErrorTranslationService } from 'src/app/services/error-translation.service';
import { DialogValidationComponent } from '../../shared/dialog/dialog-validation/dialog-validation.component';
import { EffectResult, UserRightEffects } from 'src/app/store/effects/user-right.effects';
import * as userRightActions from 'src/app/store/actions/user-right.action';
import { detailExpand, rotate } from '../../shared/animations';

@Component({
    selector: 'app-request-for-rights',
    templateUrl: './request-for-rights.component.html',
    styleUrls: ['./request-for-rights.component.scss'],
    animations: [detailExpand, rotate(180)],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class RequestForRightsComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

    /**
     * View for filter purpose
     */
    public view = 'requestForRightsView';

    /**
     * Table data
     */
    public isLoadingTable: boolean;
    // Mapping between column name and its i18n key
    public readonly COLUMN_NAME_I18N_KEY = {
        name: 'NAME',
        company: 'COMPANY',
        site: 'SITE',
        description: 'DESCRIPTION',
        level: 'ROLE',
        status: 'REQUEST_RIGHT_STATUS',
        actionColumns: ''
    };
    public displayedColumns: string[] = ['name', 'company', 'site', 'description', 'level', 'actionColumns'];
    public parentFamilies: ParentFamilyRight[];

    /**
     * For filter component
     */
    public rightsTree$: Observable<ParentFamilyRight[]>;

    public dataSource = new MatTableDataSource<ParentFamilyRight>();

    /**
     * Families and parent families manager by family / parent ID
     */
    public familiesManagers: FamilyManagers[];

    /**
     * Groups
     */
    public user$: Observable<User>;
    public qtyGroupsToDisplay = 5;
    public readonly GROUP_ACTIONS = UserGroupActions;

    private _subscription: Subscription = new Subscription();

    constructor(private userRightService: UserRightService,
        private parentFamilyRightService: ParentFamilyService,
        private familyService: FamilyService,
        private cd: ChangeDetectorRef,
        public translate: TranslateService,
        private _errorTranslationService: ErrorTranslationService,
        private store: Store,
        private dialog: MatDialog,
        private _userRightEffect: UserRightEffects
    ) { }

    ngOnInit() {
        this.userRightService.checkUserLoaded();
        this.isLoadingTable = true;

        // assign the observable for the filter input
        this.rightsTree$ = this.store.select(selectMyRightsTree$);

        this.user$ = this.store.select(selectAllUsers$).pipe(
          map(allUsers => allUsers.find(
            user => user.userId.toLowerCase() === this.userRightService.getUserNgId().toLowerCase()
          )),
        );

        // Subscribe to right requests and families managers

        this._subscription.add(
            this.store.select(selectRightRequests$).subscribe((rightRequests) => {
                // filter pending request on which have been sent by the current user in order to display the request icon
                const pendingSentRequests = rightRequests.filter(request => request.requesterUserId.toLowerCase() === this.userRightService.getUserNgId().toLowerCase());
                // dispatch parent families in order to refresh the request icon when the right requests list changes
                this.store.dispatch(new userRightActions.UpdateMyRightsTreeRequestsMapping(pendingSentRequests));
            })
        );

        this._subscription.add(
            this.store.select(selectManagers$).subscribe((managers) => {
                this.familiesManagers = managers;
            })
        );

        /* Refresh data on init */
        this.store.dispatch(new userRightActions.LoadParentFamilyRight());
        this._subscription.add(
            forkJoin([
                this.userRightService.getPendingRequests(),
                this.parentFamilyRightService.getParentFamiliesRights(),
                this.familyService.getFamiliesRights()
            ]).subscribe(([rightRequests, parentFamiliesWithRights, familiesWithRights]) => {
                // parent families right need the right requests in order to display request icon in the requestForRights screen
                this.store.dispatch(new userRightActions.SuccessRightRequest(rightRequests));
                // filter the requests sent by the current user
                const sentRightRequests = rightRequests.filter(request => request.requesterUserId.toLowerCase() === this.userRightService.getUserNgId().toLowerCase());
                // dispatch
                this.store.dispatch(new userRightActions.SuccessMyRightsTree(parentFamiliesWithRights, familiesWithRights.createdData));
                // map sent requests with parent families and families in order to display the corresponding icon
                this.store.dispatch(new userRightActions.UpdateMyRightsTreeRequestsMapping(sentRightRequests));
            })
        );
    }

    ngAfterViewInit(): void {
        this._initializeTable();

        this._subscription.add(this.dataSource.connect().subscribe((data) => {
            if (data.length) {
                this.isLoadingTable = false;
                this.cd.markForCheck();
            }
        }));
    }

    /**
     * Triggered when the user click on a parent family row
     */
    public onClickParentFamilyRow(element: ParentFamilyRight) {
        element.expanded = !element.expanded;
        this.cd.markForCheck();
    }

    /**
     * Fired when the user click on the Ask for role menu button
     * @param parentFamily Parent Family
     * @param family Family, when the request targets a family (optional)
     */
    public onClickAskRightButton(parentFamily: ParentFamilyRight, family?: FamilyRight) {
        const familyId = family?.id;
        const parentFamilyId = parentFamily.id;
        const famOrParentId = familyId ? familyId : parentFamilyId;
        // check if family managers are available
        const familyManagers = this.familiesManagers.find(el => el.familyId === famOrParentId);
        // if not, call the service method
        if (!familyManagers) {
            parentFamily.isActionLoading = true;
            if (family) { family.isActionLoading = true; }

            this._subscription.add(
                this.userRightService.getManagers(parentFamily.id, family?.id).pipe(
                  catchError(error => {
                    parentFamily.isActionLoading = false;
                    if (family) { family.isActionLoading = false; }
                    this._errorTranslationService.handleError(error, this.translate.instant('INTERNAL_ERROR'));
                    return throwError(error);
                  }),
                ).subscribe((managers) => {

                    parentFamily.isActionLoading = false;
                    if (family) { family.isActionLoading = false; }
                    // then open the dialog
                    this._handleAskRoleDialog(parentFamily, managers, family);
                })
            );
        } else {
            // use the family manager list available
            this._handleAskRoleDialog(parentFamily, familyManagers.managers, family);
        }
        this.cd.markForCheck();
    }

    /**
     * Fired on click on cancel request action menu button
     */
    public onClickCancelRequestButton(parentFamily: ParentFamilyRight, family?: FamilyRight) {
        parentFamily.isActionLoading = true;
        if (family) {
            family.isActionLoading = true;
        }
        const requestId = family ? family.requestId : parentFamily.requestId;
        this._subscription.add(
            this.userRightService.cancelRequest(requestId).subscribe(
                () => {
                    parentFamily.isActionLoading = false;
                    if (family) {
                        family.isActionLoading = false;
                    }
                }
            )
        );
    }

    /**
     * Called when the user change the paginator page
     * - Aims to auto scroll to the top of the table
     */
    public scrollUp() {
        const table = document.querySelector('.mat-table');
        table.scrollIntoView({ behavior: 'smooth' });
        window.scrollTo(0, 0);
    }

    /**
     * Initialize table data
     */
    private _initializeTable() {
        this.isLoadingTable = true;
        this.cd.markForCheck();


        this._subscription.add(combineLatest([
            this.store.select(selectParentFamilyRightFilters$),
            this.store.select(selectSiteState$),
            this.store.select(selectCompaniesState$)
        ]).pipe(
            filter(([, sites, companies]) => sites.loaded && companies.loaded)
        ).subscribe(([parentFamiliesRight, sites, companies]) => {
            const parentFamiliesRightDeepCopy = cloneDeep(parentFamiliesRight);
            parentFamiliesRightDeepCopy.forEach(parentFamilyRight => {
                parentFamilyRight.site = sites.data.find(site => site.id === parentFamilyRight.siteId);
                parentFamilyRight.company = companies.data.find(company => company.id === parentFamilyRight.site.companyId);
                parentFamilyRight.families = parentFamilyRight.families.map(familyRight => {
                    return { ...familyRight, site: parentFamilyRight.site, company: parentFamilyRight.company };
                });
                parentFamilyRight.familyFromGroup = parentFamilyRight.families.some(
                  ({ fromGroupId }) => fromGroupId
                );
            });
            this.dataSource.data = parentFamiliesRightDeepCopy;
            this.dataSource.paginator = this.paginator;
        }));

        // sorting
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'name': return item.name;
                case 'description': return item.description;
                case 'company': return item.company.name;
                case 'site': return item.site.name;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
    }

    /**
    * handle the data to pass to the dialog in order to choose the role level and the manager
    */
    private _handleAskRoleDialog(parentFamily: ParentFamilyRight, managers: User[], family?: FamilyRight) {
        const dialogData: AskRightDialogData = {
            familyName: family ? family.name : parentFamily.name,
            managers: managers,
            level: family ? family.level : parentFamily.level
        };
        const dialog = this._presentAskRoleDialog({ data: dialogData, autoFocus: false });

        this._subscription.add(dialog.afterClosed().subscribe(
            (dialogResult: AskRightDialogResult) => {

                if (dialogResult?.action === 'confirm') {
                    parentFamily.isActionLoading = true;
                    if (family) {
                        family.isActionLoading = true;
                    }
                    const selectedManager = dialogResult.manager;
                    this._subscription.add(
                        this.userRightService.requestRight(selectedManager.id, dialogResult.level, parentFamily.id, parentFamily.scope, family?.id).subscribe(
                            () => {
                                parentFamily.isActionLoading = false;
                                if (family) {
                                    family.isActionLoading = false;
                                }
                                this.cd.markForCheck();
                            }
                        )
                    );
                } else {
                    parentFamily.isActionLoading = false;
                    if (family) {
                        family.isActionLoading = false;
                    }
                }
                this.cd.markForCheck();
            }
        ));
    }

    /**
     * Open the dialog in order to choose the role level and the manager
     */
    private _presentAskRoleDialog(dialogData: MatDialogConfig<AskRightDialogData>) {
        return this.dialog.open(DialogAskRoleComponent, dialogData);
    }

    /**
     * Open the dialog for removing the roles
     */
    public onClickRemoveRoleButton(isParentFamily: boolean, familiesData: ParentFamilyRight | FamilyRight) {
        //  Open a dialog box
        const dialogRef: MatDialogRef<DialogValidationComponent> = this.dialog.open(DialogValidationComponent, {
            data: {
                title: this.translate.instant('MANAGE_RIGHT_REMOVE_ROLE'),
                textContent: this.translate.instant('MANAGE_RIGHT_REMOVE_ROLE_TEXT', {
                    familyName: familiesData.name,
                }),
                validateText: this.translate.instant('CONFIRM'),
                cancelText: this.translate.instant('CANCEL'),
                dangerousAction: true,
            }
        });
        this._subscription.add(dialogRef.componentInstance.onValidateEvent.pipe(
            switchMap(() => this.removeRolesOfUser$(familiesData, isParentFamily)),
        ).subscribe(
            (result) => {
                if (result === "success") {
                    dialogRef?.close();
                }
                this.cd.markForCheck();
            }
        ));
    }

    public removeRolesOfUser$(familiesData: ParentFamilyRight|FamilyRight, isParentFamily?: boolean): Observable<EffectResult["result"]> {
        const prepareData: DeleteMyRightsPayload = {
            isParentFamily: isParentFamily,
            deleteMyRight: this._prepareDeleteMyRightData(familiesData, isParentFamily)
        };
        this.store.dispatch(new userRightActions.RemoveMyRightsRequest({ removeRight: prepareData }));
        return this._userRightEffect.effectSubject.pipe(
            filter(result => result.action === userRightActions.ActionTypes.DELETE_MY_RIGHT),
            take(1),
            map(({ result  }) => result),
        )
    }


    /**
     * Prepare a data for removing role from Parentfamily or Family
     */

    private _prepareDeleteMyRightData(family: RemoveRolesFromParentFamilyOrFamily, isparentFamily: boolean): RemoveRolesFromParentFamilyOrFamily {
        const removeRole: RemoveRolesFromParentFamilyOrFamily = {
            id: family.id,
            name: family.name,
            scope: family.scope,
            description: family.description,
            parentFamilyId: isparentFamily ? undefined : family.parentFamilyId
        };
        return removeRole;
    }

    ngOnDestroy() {
        // Collapse rows in order to avoid visual issue
        this.dataSource?.data?.forEach((parent: ParentFamilyRight) => parent.expanded = false);
        this._subscription.unsubscribe();
    }
}
